import { createOrUpdateUserFavorites } from 'src/graphql/mutations';
import { listUserFavorites, listUserPreferencesDetails } from 'src/graphql/queries';
import {
  CreateOrUpdateUserFavoritesInput,
  MutationResponse,
  UserFavoritesResponse,
  UserPreferencesDetails,
} from '../../types/userPreferences/userPreferences.types';
import { formatApiError } from '../../utils/errorUtils';
import { logger } from '../../utils/logger';
import { fetchGraphQLRequest } from './clients/graphqlClient';

/**
 * Fetches user preferences from the GraphQL API
 * @returns Promise resolving to user preferences details
 * @throws Error if API call fails
 */
export const fetchUserPreferencesAPI = async (): Promise<UserPreferencesDetails> => {
  try {
    logger.info('Fetching user preferences from GraphQL API');

    const result = await fetchGraphQLRequest<{ listUserPreferencesDetails: UserPreferencesDetails }>({
      query: listUserPreferencesDetails,
      variables: {},
      requestName: 'listUserPreferencesDetails',
      requestType: 'query',
    });

    const preferences = result.listUserPreferencesDetails;
    logger.info('Fetched user preferences from API', { preferences });
    return preferences;
  } catch (error) {
    logger.error('Error fetching user preferences from GraphQL API:', { error });
    throw formatApiError('Failed to fetch user preferences', error);
  }
};

/**
 * Fetches user favorites from the GraphQL API
 * Returns simplified response with only IDs grouped by type
 *
 * @returns Promise resolving to UserFavoritesResponse with product_line_ids, rpn_ids, fpn_ids
 * @throws Error if API call fails
 */
export const fetchUserFavoritesAPI = async (): Promise<UserFavoritesResponse> => {
  try {
    logger.info('Fetching user favorites from GraphQL API');

    const result = await fetchGraphQLRequest<{ listUserFavorites: UserFavoritesResponse }>({
      query: listUserFavorites,
      variables: {},
      requestName: 'listUserFavorites',
      requestType: 'query',
    });

    logger.info('Fetched user favorites from API', { favorites: result.listUserFavorites });
    return result.listUserFavorites;
  } catch (error) {
    logger.error('Error fetching user favorites from GraphQL API:', { error });
    throw formatApiError('Failed to fetch user favorites', error);
  }
};

/**
 * Updates user favorites by sending the complete favorites data to the backend
 *
 * @param input - The complete favorites data with product_line_ids, rpn_ids, fpn_ids
 * @returns Promise resolving to MutationResponse with numberOfRecordsUpdated
 * @throws Error if API call fails
 *
 * @remarks
 * The backend expects the entire favorites state to be sent on each update.
 * The caller is responsible for computing the new state (adding/removing items)
 * before calling this API.
 */
export const updateUserFavoritesAPI = async (input: CreateOrUpdateUserFavoritesInput): Promise<MutationResponse> => {
  try {
    logger.info('Updating user favorites via GraphQL API', { input });

    const result = await fetchGraphQLRequest<{ createOrUpdateUserFavorites: MutationResponse }>({
      query: createOrUpdateUserFavorites,
      variables: { input },
      requestName: 'createOrUpdateUserFavorites',
      requestType: 'mutation',
      auditConfig: { pageName: 'user_favorites' },
    });

    logger.info('Updated user favorites successfully', { result: result.createOrUpdateUserFavorites });
    return result.createOrUpdateUserFavorites;
  } catch (error) {
    logger.error('Error updating user favorites via GraphQL API:', { error });
    throw formatApiError('Failed to update user favorites', error);
  }
};

// TODO: Implement update user preferences API call
// export const updateUserPreferencesAPI = async (mutationData: UserPreferencesInput): Promise<UserPreferencesDetails> => { ... }
